// ============================================================================
// MANAGER DASHBOARD
// ============================================================================
// Overview dashboard. Top section is a row of focused widgets; the
// Hours-Logged-vs-Minimum bar chart sits at the bottom (full width) since it
// can grow tall when many employees fall short of their minimum.
//
// Widgets:
// - Pending Timesheets (count + list)            — week-based
// - Submitted Timesheets (this week vs last)     — week-based
// - Hours by Charge Type (pie: C / NCP / NCNP)   — week-based
// - WIP Summary (bar chart by client/type)       — snapshot
// - Hours Logged vs Minimum (full-width bottom)  — week-based; defaults to
//   showing only employees below their minimum, with a "Show all" toggle.
//
// Week-based widgets react to the Period selector (Current/Last/2-3-4 weeks ago).
// Manager dropdown is server-scoped via SyutScopedEmployeeSelect (view ladder).

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import { SyutSelect, SyutScopedEmployeeSelect } from 'components/ui';
import { useEmployees } from 'hooks/api/useEmployee';
import { useAuthContext } from 'contexts/AuthContext';
import PendingTimesheetsWidget from './widgets/PendingTimesheetsWidget';
import SubmittedTimesheetsWidget from './widgets/SubmittedTimesheetsWidget';
import HoursVsMinimumWidget from './widgets/HoursVsMinimumWidget';
import ChargeTypeMixWidget from './widgets/ChargeTypeMixWidget';
import WipSummaryWidget from './widgets/WipSummaryWidget';
import './dashboard.css';

// ---------------------------------------------------------------------------
// Week calculation — Wednesday-based (must match backend)
// ---------------------------------------------------------------------------

const WEEK_START_DAY = 3; // 0=Sun..6=Sat → Wednesday

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getWeekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (d.getDay() - WEEK_START_DAY + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function formatWeekLabel(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(weekStart.getDate()).padStart(2, '0')} ${months[weekStart.getMonth()]} - ${String(end.getDate()).padStart(2, '0')} ${months[end.getMonth()]} ${end.getFullYear()}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ManagerDashboardScreenProps {
  /** Module-action codes granted to this user for the manager-dashboard module. */
  allowedActions?: string[];
}

const ManagerDashboardScreen: React.FC<ManagerDashboardScreenProps> = ({ allowedActions = [] }) => {
  const { user } = useAuthContext();

  // Permission gating drives only the DEFAULT selection here. The actual list
  // of pickable managers comes from /Employees/visible?moduleId=manager-dashboard,
  // which the backend filters via ReportScopeGuard.GetScopeAsync.
  const canViewAll  = allowedActions.includes('view-all-employees');
  const canViewTeam = allowedActions.includes('view-own-team');

  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  // weekOffset: 0 = current, -1 = last week, -2 = 2 weeks ago, etc.
  const [weekOffset, setWeekOffset] = useState<number>(0);

  // Resolve the logged-in user → their employee row, used only as the default
  // anchor when canViewAll is false. The dropdown's option set is loaded
  // server-side by SyutScopedEmployeeSelect — we no longer build it here.
  const { data: selfEmployees } = useEmployees({ pageSize: 1, userId: user?.userId });
  const currentEmployee = useMemo(() => selfEmployees[0] || null, [selfEmployees]);

  // Default the Manager filter:
  //   canViewAll  → '' (means "All organization-wide")
  //   canViewTeam → the logged-in manager's own employee id
  //   neither     → still defaults to self id; backend will reject anything else
  useEffect(() => {
    if (selectedManagerId) return;
    if (canViewAll) return;
    if (currentEmployee?.id) setSelectedManagerId(currentEmployee.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id, canViewAll]);

  // The Manager dropdown is locked when the caller has neither view-all nor
  // view-own-team — they can only see their own slice.
  const isManagerDropdownLocked = !canViewAll && !canViewTeam;

  // Seed option for label resolution in the controlled dropdown — without it,
  // the SyutSelect would render blank for the auto-selected self id until the
  // first async load resolves.
  const seedSelfOption = useMemo(() => {
    if (!currentEmployee) return undefined;
    return [{
      value: currentEmployee.id,
      label: `${currentEmployee.name || ''}${currentEmployee.code ? ` (${currentEmployee.code})` : ''}`.trim(),
    }];
  }, [currentEmployee]);

  // Build the last 6 weeks as period options
  const periodOptions = useMemo(() => {
    const currentWeek = getWeekStart(new Date());
    const opts: { value: string; label: string }[] = [];
    for (let i = 0; i >= -5; i--) {
      const ws = new Date(currentWeek);
      ws.setDate(ws.getDate() + i * 7);
      const label = i === 0 ? `Current Week (${formatWeekLabel(ws)})`
                 : i === -1 ? `Last Week (${formatWeekLabel(ws)})`
                 : `${Math.abs(i)} Weeks Ago (${formatWeekLabel(ws)})`;
      opts.push({ value: String(i), label });
    }
    return opts;
  }, []);

  // Compute selected week start date
  const selectedWeekStart = useMemo(() => {
    const currentWeek = getWeekStart(new Date());
    const ws = new Date(currentWeek);
    ws.setDate(ws.getDate() + weekOffset * 7);
    return toLocalDateStr(ws);
  }, [weekOffset]);

  const managerId = selectedManagerId || undefined;

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Manager Dashboard"
        description="Team overview — timesheet submissions, engagement status, and WIP summary"
        icon={<IconSystem.dashboard size={18} />}
        customToolbarItems={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ minWidth: 220 }}>
              <SyutSelect
                options={periodOptions}
                value={String(weekOffset)}
                onChange={(val) => setWeekOffset(Number(val ?? 0))}
                isClearable={false}
                size="sm"
                placeholder="Period"
              />
            </div>
            <div style={{ minWidth: 240 }}>
              <SyutScopedEmployeeSelect
                scopeMode="view"
                moduleId="manager-dashboard"
                role="manager"
                value={selectedManagerId}
                onChange={(val) => setSelectedManagerId(val || '')}
                isClearable={canViewAll}
                disabled={isManagerDropdownLocked}
                seedOptions={seedSelfOption}
                placeholder={canViewAll ? 'Scope: All managers' : 'Scope: My team'}
              />
            </div>
          </div>
        }
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, padding: '0.75rem' }}>
          <div className="dashboard-grid">
            <PendingTimesheetsWidget managerId={managerId} weekStartDate={selectedWeekStart} />
            <SubmittedTimesheetsWidget managerId={managerId} weekStartDate={selectedWeekStart} />
            <ChargeTypeMixWidget managerId={managerId} weekStartDate={selectedWeekStart} />
            <WipSummaryWidget managerId={managerId} weekStartDate={selectedWeekStart} />
            <HoursVsMinimumWidget managerId={managerId} weekStartDate={selectedWeekStart} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboardScreen;
